import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { DeepPartial, FormProvider } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { LinearProgress } from 'shared/mui/LinearProgress/LinearProgress';
import { Stack } from 'shared/mui/Stack/Stack';
import { Tooltip } from 'shared/mui/Tooltip';
import { Typography } from 'shared/mui/Typography';
import { BooleanInput, AutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { fieldWidth } from 'themes/layout';
import { ComparisonOperator, ExpressionParam } from 'types/embedded/gatewayRule';

import { GatewayRulesDictionaryProvider } from './context/GatewayRulesDictionaryContext';
import { useGatewayRulesFormContext } from './context/GatewayRulesFormContext';
import { GatewayRulesMerchantDictionaryProvider } from './context/GatewayRulesMerchantDictionaryContext';
import {
  StyledContent,
  StyledFormContent,
  StyledMappingCardContent,
} from './GatewayRulesForm.styled';
import { GatewayRulesFormMapping } from './GatewayRulesFormMapping';
import { GatewayRulesFormRule } from './GatewayRulesFormRule';
import { GatewayRulesFormSkeletonHead } from './GatewayRulesFormSkeleton/GatewayRulesFormSkeletonHead';
import { useGatewayRulesForm, useInitialExpression, useMappingField } from './hooks';
import { GatewayRulesFormValues, MappingItemFormValues } from './hooks/useGatewayRulesForm';
import { getInitialMappingItem } from './utils';

export type Expression = {
  id?: string;
  expressionParam?: ExpressionParam;
  comparisonOperator?: ComparisonOperator;
  value?: string[];
};

export type ConditionGroup = {
  parent: null | string;
  isAnd: boolean;
  expressions: Expression[];
  children: ConditionGroup[];
};

type Props = {
  children: React.ReactNode;
  onSubmit: (data: GatewayRulesFormValues) => void;
  defaultValues?: DeepPartial<GatewayRulesFormValues>;
};

export function GatewayRulesForm(props: Props): JSX.Element {
  const translate = useTypedTranslate();
  const { expressionParams, loadingExpressionParams } = useGatewayRulesFormContext();

  const { onSubmit, form } = useGatewayRulesForm({
    defaultValues: props.defaultValues,
    onSubmit: props.onSubmit,
  });

  const { mappingFields, onRemoveMappingClick, onAddMappingClick } = useMappingField({
    name: 'mappings',
    form,
  });

  const active = form.watch('active');

  const [isDisabledAddCondition, setDisabledAddCondition] = useState<boolean>(
    Boolean(props.defaultValues?.conditionGroup),
  );
  const { initialExpression, isLoading: isLoadingExpression } =
    useInitialExpression(expressionParams);

  function onAddConditionClick(): void {
    form.setValue('conditionGroup', {
      isAnd: true,
      expressions: [initialExpression],
    } as ConditionGroup);
    setDisabledAddCondition(true);
  }

  function onRemoveConditionClick(): void {
    form.unregister('conditionGroup');
    form.setValue('conditionGroup', null, { shouldDirty: true });
    setDisabledAddCondition(false);
  }

  function onChangeMethod(mappingIndex: number): void {
    // @ts-ignore
    form.setValue(`mappings.${mappingIndex}.direction`, null, { shouldDirty: true });
    form.setValue(`mappings.${mappingIndex}.mappingItems`, [
      getInitialMappingItem() as MappingItemFormValues,
    ]);
    return;
  }

  function onChangeMerchant(): void {
    form.getValues('mappings')?.forEach((_, mappingIndex) => {
      form.setValue(`mappings.${mappingIndex}.method`, null as unknown as string, {
        shouldDirty: true,
      });
      onChangeMethod(mappingIndex);
    });
  }

  function onChangeDirection(mappingIndex: number): void {
    form.setValue(`mappings.${mappingIndex}.mappingItems`, [
      getInitialMappingItem() as MappingItemFormValues,
    ]);
    return;
  }

  return (
    <FormProvider {...form}>
      <GatewayRulesDictionaryProvider>
        <GatewayRulesMerchantDictionaryProvider>
          <Stack alignItems="flex-start" sx={{ form: { width: '100%' } }}>
            <form noValidate onSubmit={onSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <StyledFormContent>
                  {isLoadingExpression ? (
                    <GatewayRulesFormSkeletonHead />
                  ) : (
                    <>
                      <TextInput
                        isRequired
                        label={translate('app.table.name')}
                        name="name"
                        size="small"
                        source="name"
                        sx={{ ...fieldWidth.medium, marginTop: '0' }}
                        variant="outlined"
                      />
                      <ReferenceInput
                        filter={{ archive: false }}
                        perPage={1000}
                        reference="merchants"
                        source="merchant"
                      >
                        <AutocompleteInput
                          filterToQuery={(searchText: string) => ({ name: searchText })}
                          isRequired
                          label={translate('app.table.merchant')}
                          onChange={onChangeMerchant}
                          optionText="name"
                          size="small"
                          sx={fieldWidth.medium}
                          variant="outlined"
                        />
                      </ReferenceInput>
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          active
                            ? translate('app.gatewayRules.ruleIsActive')
                            : translate('app.gatewayRules.ruleIsInactive')
                        }
                      >
                        <Box mt={1}>
                          <BooleanInput
                            label={translate('app.gatewayRules.active')}
                            source="active"
                          />
                        </Box>
                      </Tooltip>
                    </>
                  )}
                </StyledFormContent>
                <StyledFormContent>
                  <Typography component="h2" fontWeight={500} variant="body1">
                    {translate('app.gatewayRules.addCondition')}
                  </Typography>
                </StyledFormContent>
                {!isDisabledAddCondition && (
                  <StyledContent>
                    {loadingExpressionParams ? (
                      <LinearProgress />
                    ) : (
                      <Alert
                        action={
                          <Button
                            disabled={isDisabledAddCondition || loadingExpressionParams}
                            label={translate('app.gatewayRules.addConditionBtn')}
                            onClick={onAddConditionClick}
                            startIcon={<AddIcon />}
                            variant="text"
                          />
                        }
                        severity="info"
                      >
                        {translate('app.gatewayRules.addConditionWarn')}
                      </Alert>
                    )}
                  </StyledContent>
                )}
                {isDisabledAddCondition && (
                  <StyledContent>
                    <GatewayRulesFormRule
                      className="tree"
                      onRemoveConditionClick={onRemoveConditionClick}
                    />
                  </StyledContent>
                )}
                <StyledFormContent>
                  <Typography component="h2" fontWeight={500} variant="body1">
                    {translate('app.gatewayRules.addMappings')}
                  </Typography>
                </StyledFormContent>
                <StyledMappingCardContent>
                  {mappingFields.map((mapping, mappingIndex) => {
                    return (
                      <GatewayRulesFormMapping
                        key={`${mapping.id}-${mappingIndex}`}
                        mappingIndex={mappingIndex}
                        onChangeDirection={onChangeDirection}
                        onChangeMethod={onChangeMethod}
                        onRemoveMappingClick={onRemoveMappingClick}
                      />
                    );
                  })}
                </StyledMappingCardContent>
                <Button
                  label={translate('app.gatewayRules.addMappingBtn')}
                  onClick={onAddMappingClick}
                  startIcon={<AddIcon />}
                  sx={{ margin: '16px 0px 24px' }}
                  variant="outlined"
                />
                {props.children}
              </Box>
            </form>
          </Stack>
        </GatewayRulesMerchantDictionaryProvider>
      </GatewayRulesDictionaryProvider>
    </FormProvider>
  );
}
